/**
 * Link a bike to an investor, and manage the bikes linked to them.
 *
 * A link is ownership from a date: the investor's share of each week the bike
 * is ridden from then on is theirs. One owner at a time — a bike another
 * investor owns is shown but cannot be chosen until that link is ended.
 * Linking never moves a payment already recorded.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { BIKE_OWNERSHIP_LABELS, type BikeView, type InvestorBikeLink, type InvestorDetail } from '@cenox/shared';
import { Button, ErrorState, Input, Modal, Select, TableSkeleton, cn } from '../../components/ui';
import { ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { money, todayIn } from '../../lib/format';
import { useBikes } from './data';
import { CorrectLinkDialog } from './CorrectLinkDialog';
import {
  describeSplitChange,
  useEndInvestorBike,
  useInvestor,
  useLinkInvestorBike,
  useSetInvestorEntitlement,
} from './investorData';

function ownerText(bike: Pick<BikeView, 'ownership' | 'investorName' | 'ownerName'>): string {
  const kind = BIKE_OWNERSHIP_LABELS[bike.ownership];
  const name = bike.ownership === 'investor' ? (bike.investorName ?? bike.ownerName) : bike.ownerName;
  return name ? `${kind} · ${name}` : kind;
}

export function LinkBikeDialog({
  investorId,
  investmentId,
  onClose,
  onDone,
}: {
  investorId: string;
  /** The vehicle partnership the bike joins; the investor's only active one when left out. */
  investmentId?: string;
  onClose: () => void;
  onDone: (message: string) => void;
}): ReactNode {
  const { business } = useAuth();
  const currency = business?.currencyCode ?? 'NGN';
  const today = todayIn(business?.timezone ?? 'Africa/Lagos');
  const investor = useInvestor(investorId);
  const bikes = useBikes();
  const link = useLinkInvestorBike();
  const [search, setSearch] = useState('');
  const [bikeId, setBikeId] = useState('');
  const [effectiveFrom, setEffectiveFrom] = useState(today);
  const [weeklyAmount, setWeeklyAmount] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const fields = link.error instanceof ApiError ? link.error.fields : undefined;
  const name = investor.data?.name ?? 'the investor';
  const share = weeklyAmount ?? investor.data?.defaultWeeklyShare ?? '';

  const list = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (bikes.data?.bikes ?? []).filter(
      (b) => !q || b.registration.toLowerCase().includes(q) || (b.riderName ?? '').toLowerCase().includes(q),
    );
  }, [bikes.data, search]);

  return (
    <Modal open onClose={onClose} title={`Link a bike to ${name}`} size="lg">
      <form
        aria-label="Link bike"
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!bikeId) return;
          const chosen = list.find((b) => b.id === bikeId) ?? bikes.data?.bikes.find((b) => b.id === bikeId);
          link.mutate(
            {
              investorId,
              // A share typed here is this bike's agreement; left alone, the
              // partnership's own terms apply (a fixed amount or a percentage),
              // else the business default.
              input: {
                bikeId,
                effectiveFrom,
                reason,
                ...(investmentId ? { investmentId } : {}),
                ...(weeklyAmount !== null && weeklyAmount.trim() ? { weeklyAmount } : {}),
              },
            },
            {
              onSuccess: (r) =>
                onDone(
                  r.replayed
                    ? `${chosen?.registration ?? 'The bike'} was already linked to ${name} from ${effectiveFrom}.`
                    : `${chosen?.registration ?? 'The bike'} is linked to ${name} from ${effectiveFrom}. ${describeSplitChange(r)}`,
                ),
            },
          );
        }}
      >
        <Input
          label="Find a bike"
          type="search"
          placeholder="Registration or rider"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {bikes.isPending ? (
          <TableSkeleton rows={3} columns={3} />
        ) : list.length === 0 ? (
          <p className="text-sm text-slate-500">No bike on the register matches. Add the bike in Bike Register first.</p>
        ) : (
          <fieldset>
            <legend className="mb-1.5 text-sm font-medium text-slate-700">Bike</legend>
            <ul className="max-h-64 divide-y divide-slate-100 overflow-y-auto rounded-lg border border-slate-200" aria-label="Bikes on the register">
              {list.map((b) => {
                const otherInvestor = b.ownership === 'investor' && b.investorId !== investorId;
                const already = b.ownership === 'investor' && b.investorId === investorId;
                const disabled = otherInvestor || already;
                return (
                  <li key={b.id}>
                    <label
                      className={cn(
                        'flex cursor-pointer items-start gap-3 px-3 py-2 text-sm',
                        disabled ? 'cursor-not-allowed opacity-60' : 'hover:bg-slate-50',
                        bikeId === b.id && 'bg-indigo-50',
                      )}
                    >
                      <input
                        type="radio"
                        name="bike"
                        className="mt-1 size-4"
                        value={b.id}
                        checked={bikeId === b.id}
                        disabled={disabled}
                        onChange={() => setBikeId(b.id)}
                        aria-label={b.registration}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="font-medium text-slate-900">{b.registration}</span>
                        <span className="block text-xs text-slate-500">
                          Rider: {b.riderName ?? 'none'} · Owner: {ownerText(b)}
                        </span>
                        {otherInvestor ? (
                          <span className="block text-xs text-amber-700">
                            Owned by {b.investorName ?? 'another investor'} — end that link first
                          </span>
                        ) : already ? (
                          <span className="block text-xs text-emerald-700">Already linked</span>
                        ) : null}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
            {fields?.bikeId ? (
              <p role="alert" className="mt-1 text-xs text-red-700">
                {fields.bikeId[0]}
              </p>
            ) : null}
          </fieldset>
        )}

        <div className="grid gap-3 sm:grid-cols-3">
          <Input
            label="Owned from"
            type="date"
            required
            value={effectiveFrom}
            onChange={(e) => setEffectiveFrom(e.target.value)}
            hint="Weeks ending on or after this date"
            error={fields?.effectiveFrom?.[0]}
          />
          <Input
            label={`Investor's share a week (${currency})`}
            inputMode="decimal"
            value={share}
            onChange={(e) => setWeeklyAmount(e.target.value)}
            hint={`Leave as is for the partnership’s terms, else the default ${money(investor.data?.defaultWeeklyShare ?? '0', currency)}`}
            error={fields?.weeklyAmount?.[0]}
          />
          <Input
            label="Reason"
            required
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Bought by the investor"
            error={fields?.reason?.[0]}
          />
        </div>
        <p className="text-xs text-slate-500">
          Unpaid weeks on this bike from the date move to {name}. Payments already recorded stay exactly as they were
          split.
        </p>
        {link.error && (!fields || fields.bikeId) ? <ErrorState message={link.error.message} /> : null}
        <div className="flex flex-wrap justify-end gap-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={link.isPending}>
            Cancel
          </Button>
          <Button type="submit" loading={link.isPending} disabled={link.isPending || !bikeId || !reason.trim()}>
            Link bike
          </Button>
        </div>
      </form>
    </Modal>
  );
}

export function ManageLinkedBikesDialog({
  investor,
  currency,
  onClose,
  onLink,
}: {
  investor: InvestorDetail;
  currency: string;
  onClose: () => void;
  onLink: () => void;
}): ReactNode {
  const [editing, setEditing] = useState<{ link: InvestorBikeLink; mode: 'share' | 'end' } | null>(null);
  const [correcting, setCorrecting] = useState<InvestorBikeLink | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const current = investor.links.filter((l) => l.current || (l.effectiveTo === null));
  const past = investor.links.filter((l) => !(l.current || l.effectiveTo === null));

  if (correcting) {
    return (
      <CorrectLinkDialog
        investorId={investor.id}
        investorName={investor.name}
        link={correcting}
        onClose={() => setCorrecting(null)}
        onDone={(m) => {
          setCorrecting(null);
          setNote(m);
        }}
      />
    );
  }

  return (
    <Modal open onClose={onClose} title={`${investor.name}'s bikes`} size="lg">
      <div className="space-y-4">
        {note ? (
          <p className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-800" role="status">
            {note}
          </p>
        ) : null}
        {current.length === 0 ? (
          <p className="text-sm text-slate-500">No bike is linked to {investor.name} now.</p>
        ) : (
          <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200" aria-label="Linked bikes">
            {current.map((l) => (
              <li key={l.periodId} className="space-y-2 px-3 py-2.5 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <span className="font-medium text-slate-900">{l.registration}</span>
                    <span className="block text-xs text-slate-500">
                      Rider: {l.riderName ?? 'none'}
                      {l.riderSince ? ` since ${l.riderSince}` : ''} · Owned from{' '}
                      {l.effectiveFrom <= '2000-01-01' ? 'registration' : l.effectiveFrom}
                      {l.effectiveTo ? ` to ${l.effectiveTo}` : ''} · {l.sharePercent ? `${l.sharePercent}% of remittance` : `${money(l.weeklyShare, currency)} a week`} (
                      {l.shareSource === 'agreement' ? 'agreement' : 'default'})
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="secondary" onClick={() => setCorrecting(l)}>
                      Edit agreement / Start date
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => setEditing({ link: l, mode: 'share' })}>
                      Change share
                    </Button>
                    <Button size="sm" variant="secondary" onClick={() => setEditing({ link: l, mode: 'end' })}>
                      End link
                    </Button>
                  </div>
                </div>
                {editing?.link.periodId === l.periodId ? (
                  editing.mode === 'share' ? (
                    <ShareForm
                      link={l}
                      currency={currency}
                      onCancel={() => setEditing(null)}
                      onDone={(m) => {
                        setEditing(null);
                        setNote(m);
                      }}
                    />
                  ) : (
                    <EndForm
                      investorId={investor.id}
                      link={l}
                      onCancel={() => setEditing(null)}
                      onDone={(m) => {
                        setEditing(null);
                        setNote(m);
                      }}
                    />
                  )
                ) : null}
              </li>
            ))}
          </ul>
        )}
        {past.length > 0 ? (
          <div>
            <p className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">Earlier</p>
            <ul className="divide-y divide-slate-100 rounded-lg border border-slate-100 text-sm" aria-label="Earlier links">
              {past.map((l) => (
                <li key={l.periodId} className="flex flex-wrap justify-between gap-2 px-3 py-2">
                  <span className="text-slate-700">{l.registration}</span>
                  <span className="text-xs text-slate-500">
                    {l.effectiveFrom <= '2000-01-01' ? 'From registration' : `From ${l.effectiveFrom}`} to {l.effectiveTo}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        <div className="flex flex-wrap justify-end gap-2 border-t border-slate-200 pt-3">
          <Button variant="secondary" onClick={onLink}>
            Link another bike
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function ShareForm({
  link,
  currency,
  onCancel,
  onDone,
}: {
  link: InvestorBikeLink;
  currency: string;
  onCancel: () => void;
  onDone: (message: string) => void;
}): ReactNode {
  const { business } = useAuth();
  const set = useSetInvestorEntitlement();
  const [weeklyAmount, setWeeklyAmount] = useState(link.weeklyShare);
  const [effectiveFrom, setEffectiveFrom] = useState(todayIn(business?.timezone ?? 'Africa/Lagos'));
  const [reason, setReason] = useState('');
  const fields = set.error instanceof ApiError ? set.error.fields : undefined;
  return (
    <form
      aria-label={`Change share on ${link.registration}`}
      className="space-y-3 rounded-lg bg-slate-50 p-3"
      onSubmit={(e) => {
        e.preventDefault();
        set.mutate(
          { bikeId: link.bikeId, input: { weeklyAmount, effectiveFrom, reason } },
          { onSuccess: (r) => onDone(`${link.registration}: ${money(weeklyAmount, currency)} a week from ${effectiveFrom}. ${describeSplitChange(r)}`) },
        );
      }}
    >
      <div className="grid gap-3 sm:grid-cols-3">
        <Input
          label={`Share a week (${currency})`}
          required
          inputMode="decimal"
          value={weeklyAmount}
          onChange={(e) => setWeeklyAmount(e.target.value)}
          error={fields?.weeklyAmount?.[0]}
        />
        <Input label="Applies from" type="date" required value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} error={fields?.effectiveFrom?.[0]} />
        <Input label="Reason" required value={reason} onChange={(e) => setReason(e.target.value)} placeholder="New agreement" error={fields?.reason?.[0]} />
      </div>
      <p className="text-xs text-slate-500">Weeks already paid keep the share they were paid under.</p>
      {set.error && !fields ? <ErrorState message={set.error.message} /> : null}
      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={set.isPending || !weeklyAmount || !reason.trim()}>
          Save share
        </Button>
      </div>
    </form>
  );
}

function EndForm({
  investorId,
  link,
  onCancel,
  onDone,
}: {
  investorId: string;
  link: InvestorBikeLink;
  onCancel: () => void;
  onDone: (message: string) => void;
}): ReactNode {
  const { business } = useAuth();
  const end = useEndInvestorBike();
  const [endsOn, setEndsOn] = useState(todayIn(business?.timezone ?? 'Africa/Lagos'));
  const [nextOwnership, setNextOwnership] = useState<'cenohub' | 'rider'>('cenohub');
  const [reason, setReason] = useState('');
  const [endCoverage, setEndCoverage] = useState(true);
  const fields = end.error instanceof ApiError ? end.error.fields : undefined;
  return (
    <form
      aria-label={`End link on ${link.registration}`}
      className="space-y-3 rounded-lg bg-slate-50 p-3"
      onSubmit={(e) => {
        e.preventDefault();
        end.mutate(
          { investorId, bikeId: link.bikeId, input: { endsOn, nextOwnership, reason, endPartnershipCoverage: endCoverage } },
          {
            onSuccess: (r) =>
              onDone(
                `${link.registration} is no longer the investor's after ${endsOn}. ${describeSplitChange(r)} ` +
                  'What was collected for them is still owed to them.',
              ),
          },
        );
      }}
    >
      <div className="grid gap-3 sm:grid-cols-3">
        <Input
          label="Last day owned"
          type="date"
          required
          min={link.effectiveFrom}
          value={endsOn}
          onChange={(e) => setEndsOn(e.target.value)}
          error={fields?.endsOn?.[0]}
        />
        <Select
          label="Owned after that by"
          value={nextOwnership}
          onChange={(e) => setNextOwnership(e.target.value as 'cenohub' | 'rider')}
          options={[
            { value: 'cenohub', label: BIKE_OWNERSHIP_LABELS.cenohub },
            { value: 'rider', label: BIKE_OWNERSHIP_LABELS.rider },
          ]}
        />
        <Input label="Reason" required value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Sold back to the company" error={fields?.reason?.[0]} />
      </div>
      <label className="flex items-start gap-2 text-sm text-slate-700">
        <input type="checkbox" className="mt-1" checked={endCoverage} onChange={(e) => setEndCoverage(e.target.checked)} />
        <span>
          Also end the investor&apos;s partnership earnings on this vehicle
          <span className="block text-xs text-slate-500">
            Untick to record the ownership change only: an active partnership keeps earning its weekly share on this vehicle.
          </span>
        </span>
      </label>
      {end.error && (!fields || fields.endsOn) ? <ErrorState message={end.error.message} /> : null}
      <div className="flex justify-end gap-2">
        <Button type="button" size="sm" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" size="sm" disabled={end.isPending || !reason.trim()}>
          End link
        </Button>
      </div>
    </form>
  );
}
